/* ============================================================================
   Mon Casque M1 — Outil de datation des jugulaires US M1
   Fichier : assets/chinstrap-dating.js

   Même architecture que helmet-dating.js :
   - le balisage (titres, questions, libellés) vit dans le corps de l'article,
     traduit par Localize ou collé en trois versions ;
   - les chaînes générées dynamiquement vivent dans l'objet I18N ci-dessous ;
   - la logique ne lit jamais le texte visible, uniquement id / name / value.

   Ordre de la logique :
   1. Fixation par clips métalliques  -> pièce d'après-guerre, on s'arrête là.
   2. Détection des incohérences      -> assemblage composite ou remontage.
   3. Détection de reproduction       -> trame non conforme.
   4. Datation par couleur + boucle + crochet.
   ========================================================================= */

(function () {
  'use strict';

  /* ---------------------------------------------------------------------
     1. DICTIONNAIRE
     --------------------------------------------------------------------- */

  var I18N = {

    fr: {
      progress: 'Question {n} sur {total}',
      selectAnswer: 'Veuillez sélectionner une réponse avant de continuer.',
      verdictLabel: 'Conclusion',
      confidenceLabel: 'Confiance : ',
      yourAnswers: 'Vos réponses',

      confVeryHigh: 'très élevée',
      confHigh: 'élevée',
      confMedium: 'moyenne',
      confLow: 'faible',

      // Intitulés des réponses, repris dans le récapitulatif
      lColor: 'Couleur de la sangle',
      lMount: 'Fixation à la coque',
      lBuckle: 'Type de boucle',
      lHook: 'Crochet en J',
      lWeave: 'Trame de la sangle',

      vOd3: 'Olive drab #3 (kaki brun)',
      vOd7: 'Olive drab #7 (vert foncé)',
      vSewn: 'Cousue directement sur les boucles',
      vClips: 'Clips métalliques amovibles',
      vMountUnknown: 'Indéterminée',
      vCast: 'Laiton moulé (type M1917A1)',
      vStamped: 'Laiton estampé, barre surélevée',
      vFlat: 'Laiton plat',
      vIron: 'Alliage de fer',
      vBuckleUnknown: 'Indéterminée',
      vSharpBrass: 'Laiton, pointe affûtée',
      vSharpSteel: 'Acier noirci, pointe affûtée',
      vSquare: 'Pointe carrée',
      vHookUnknown: 'Indéterminé',
      vWeave11: '11 rangées de 3 fils',
      vWeaveOther: 'Autre configuration',
      vWeaveUnknown: 'Indéterminée',

      // Verdicts
      pPostwar: 'Production d\u2019après-guerre',
      dPostwar: 'La fixation par clips métalliques amovibles n\u2019existe pas sur les jugulaires de la Seconde Guerre mondiale : pendant le conflit, les sangles étaient cousues directement sur les boucles de la coque. Cette pièce est donc postérieure à 1945, quelles que soient ses autres caractéristiques. Les jugulaires à clips équipent les casques M1 de la guerre de Corée et des décennies suivantes.',

      pRepro: 'Reproduction probable',
      dRepro: 'Deux signaux concordants pointent vers une fabrication moderne : la trame de la sangle ne correspond pas au tissage d\u2019origine, et les composants relevés forment une combinaison que la production de guerre n\u2019a jamais réalisée. Les reproductions récentes reprennent souvent les éléments les plus visibles sans respecter la chronologie réelle des pièces.',

      pReproPossible: 'Reproduction ou sangle de remplacement',
      dReproPossible: 'La trame de la sangle ne correspond pas aux 11 rangées de 3 fils du tissage d\u2019origine. Deux explications : une reproduction moderne, ou une sangle remplacée à l\u2019époque ou après. Les autres caractéristiques restent cohérentes entre elles, ce qui n\u2019exclut pas une pièce partiellement authentique.',

      pComposite: 'Assemblage composite ou remontage',
      dComposite: 'Les composants relevés appartiennent à des périodes de production incompatibles. Cela n\u2019indique pas nécessairement une reproduction : les jugulaires ont beaucoup circulé, et les remontages à partir de pièces d\u2019époques différentes sont fréquents, aussi bien pendant le conflit qu\u2019entre les mains des collectionneurs. La datation par composant reste possible, mais l\u2019ensemble ne forme pas une pièce homogène.',

      pVeryEarly: 'Très précoce — 1941 ou avant',
      dVeryEarly: 'La boucle en laiton moulé provient des stocks du casque M1917A1, réutilisés sur les toutes premières jugulaires M1. Associée à une sangle Olive drab #3, elle situe la production au tout début de la fabrication du M1, voire avant. Ces pièces sont rares.',

      pEarly: 'Précoce — 1941-1942',
      dEarly: 'Sangle Olive drab #3, boucle en laiton estampé à barre surélevée et crochet à pointe affûtée : la combinaison caractéristique des premières productions de série, avant la simplification de 1942.',

      pMid: 'Intermédiaire — fin 1942 à mi-1943',
      dMid: 'La boucle en laiton plat, plus simple à produire, remplace la boucle estampée fin 1942. Associée à une sangle Olive drab #3, elle place cette jugulaire avant le basculement vers l\u2019Olive drab #7 de fin 1943.',

      pMidLate: 'Intermédiaire-tardive — mi-1943 à mi-1944',
      dMidLate: 'La boucle en alliage de fer n\u2019a été produite qu\u2019entre juillet 1943 et mai 1944, pour économiser le laiton. C\u2019est le critère de datation le plus étroit de tout le questionnaire, et donc le plus fiable.',

      pLate: 'Tardive — mi-1944 à 1945',
      dLate: 'Sangle Olive drab #7 et crochet à pointe carrée : les caractéristiques des dernières productions de guerre. La boucle est en laiton, la fabrication ayant repris en juin 1944 après l\u2019abandon de l\u2019alliage de fer.',

      pOd7Generic: 'Tardive — fin 1943 à 1945',
      dOd7Generic: 'La sangle Olive drab #7 place cette jugulaire après le basculement de teinte de fin 1943. Les autres caractéristiques relevées ne permettent pas de resserrer davantage la fourchette.',

      pOd3Generic: 'Précoce à intermédiaire — 1941 à 1943',
      dOd3Generic: 'La sangle Olive drab #3 place cette jugulaire avant le basculement de teinte de fin 1943. Les autres caractéristiques relevées ne permettent pas de resserrer davantage la fourchette.',

      pUndetermined: 'Indéterminée',
      dUndetermined: 'Les caractéristiques renseignées ne suffisent pas à établir une datation. Reprenez le questionnaire en examinant la pièce en lumière naturelle, ou faites-la examiner physiquement.',

      // Notes additionnelles
      nWeaveOk: 'La trame de 11 rangées de 3 fils correspond au tissage d\u2019origine.',
      nCastOd7: 'Signal d\u2019incohérence : une boucle moulée M1917A1 sur une sangle Olive drab #7 associe un composant de 1941 ou avant à un tissu de fin 1943 au plus tôt.',
      nSharpOd7: 'Signal d\u2019incohérence : un crochet à pointe affûtée, abandonné en 1942, sur une sangle Olive drab #7 postérieure à fin 1943.',
      nIronOd7: 'Signal d\u2019incohérence mineur : la boucle en alliage de fer et la sangle Olive drab #7 ne se recoupent que sur une fenêtre très courte, fin 1943 à mai 1944.',
      nCastSquare: 'Signal d\u2019incohérence : une boucle moulée M1917A1 associée à un crochet à pointe carrée, apparu en 1942.',
      nSteelHookRare: 'Le crochet en acier noirci à pointe affûtée est une variante rare, produite en petite quantité lors des tensions sur l\u2019approvisionnement en laiton.',

      // Renvoi produit
      ctaTitle: 'Besoin d\u2019une sangle conforme ?',
      ctaText: 'Nous produisons des jugulaires de reproduction respectant le tissage, les teintes et la quincaillerie d\u2019origine, par période.',
      ctaButton: 'Voir les jugulaires'
    },

    en: {
      progress: 'Question {n} of {total}',
      selectAnswer: 'Please select an answer before continuing.',
      verdictLabel: 'Conclusion',
      confidenceLabel: 'Confidence: ',
      yourAnswers: 'Your answers',

      confVeryHigh: 'very high',
      confHigh: 'high',
      confMedium: 'medium',
      confLow: 'low',

      lColor: 'Webbing colour',
      lMount: 'Attachment to the shell',
      lBuckle: 'Buckle type',
      lHook: 'J-hook',
      lWeave: 'Webbing weave',

      vOd3: 'Olive drab #3 (brownish khaki)',
      vOd7: 'Olive drab #7 (dark green)',
      vSewn: 'Sewn directly to the loops',
      vClips: 'Removable metal clips',
      vMountUnknown: 'Undetermined',
      vCast: 'Cast brass (M1917A1 type)',
      vStamped: 'Stamped brass, raised bar',
      vFlat: 'Flat brass',
      vIron: 'Iron alloy',
      vBuckleUnknown: 'Undetermined',
      vSharpBrass: 'Brass, sharpened point',
      vSharpSteel: 'Blackened steel, sharpened point',
      vSquare: 'Square point',
      vHookUnknown: 'Undetermined',
      vWeave11: '11 rows of 3 threads',
      vWeaveOther: 'Different configuration',
      vWeaveUnknown: 'Undetermined',

      pPostwar: 'Post-war production',
      dPostwar: 'Removable metal clips do not exist on Second World War chinstraps: during the conflict, the straps were sewn directly to the shell loops. This piece is therefore later than 1945, whatever its other features. Clip-on chinstraps belong to the Korean War M1 helmets and the decades that followed.',

      pRepro: 'Likely reproduction',
      dRepro: 'Two converging signals point to modern manufacture: the webbing weave does not match the original, and the components recorded form a combination wartime production never made. Recent reproductions often copy the most visible elements without respecting the real chronology of the parts.',

      pReproPossible: 'Reproduction or replacement strap',
      dReproPossible: 'The webbing weave does not match the original 11 rows of 3 threads. Two explanations: a modern reproduction, or a strap replaced at the time or later. The other features remain consistent with each other, which does not rule out a partly authentic piece.',

      pComposite: 'Composite assembly or rebuild',
      dComposite: 'The components recorded belong to incompatible production periods. This does not necessarily mean a reproduction: chinstraps changed hands a great deal, and rebuilds from parts of different eras are common, both during the conflict and among collectors. Dating by individual component remains possible, but the assembly is not homogeneous.',

      pVeryEarly: 'Very early — 1941 or before',
      dVeryEarly: 'The cast brass buckle comes from M1917A1 helmet stocks, reused on the very first M1 chinstraps. Combined with Olive drab #3 webbing, it places production at the very start of M1 manufacture, or before. These pieces are scarce.',

      pEarly: 'Early — 1941-1942',
      dEarly: 'Olive drab #3 webbing, stamped brass buckle with raised bar and sharpened-point hook: the combination characteristic of the first series production, before the 1942 simplification.',

      pMid: 'Intermediate — late 1942 to mid-1943',
      dMid: 'The flat brass buckle, simpler to produce, replaced the stamped buckle in late 1942. Combined with Olive drab #3 webbing, it places this chinstrap before the shift to Olive drab #7 in late 1943.',

      pMidLate: 'Intermediate-late — mid-1943 to mid-1944',
      dMidLate: 'The iron alloy buckle was only produced between July 1943 and May 1944, to save brass. It is the narrowest dating criterion in the whole questionnaire, and therefore the most reliable.',

      pLate: 'Late — mid-1944 to 1945',
      dLate: 'Olive drab #7 webbing and square-point hook: the features of the last wartime production. The buckle is brass, manufacture having resumed in June 1944 after the iron alloy was dropped.',

      pOd7Generic: 'Late — late 1943 to 1945',
      dOd7Generic: 'The Olive drab #7 webbing places this chinstrap after the late-1943 shade change. The other features recorded do not allow the range to be narrowed further.',

      pOd3Generic: 'Early to intermediate — 1941 to 1943',
      dOd3Generic: 'The Olive drab #3 webbing places this chinstrap before the late-1943 shade change. The other features recorded do not allow the range to be narrowed further.',

      pUndetermined: 'Undetermined',
      dUndetermined: 'The features supplied are not sufficient to establish a dating. Run through the questionnaire again examining the piece in natural light, or have it inspected physically.',

      nWeaveOk: 'The 11 rows of 3 threads match the original weave.',
      nCastOd7: 'Inconsistency: an M1917A1 cast buckle on Olive drab #7 webbing pairs a component from 1941 or earlier with fabric from late 1943 at the earliest.',
      nSharpOd7: 'Inconsistency: a sharpened-point hook, dropped in 1942, on Olive drab #7 webbing later than late 1943.',
      nIronOd7: 'Minor inconsistency: the iron alloy buckle and Olive drab #7 webbing only overlap in a very short window, late 1943 to May 1944.',
      nCastSquare: 'Inconsistency: an M1917A1 cast buckle paired with a square-point hook, introduced in 1942.',
      nSteelHookRare: 'The blackened steel hook with sharpened point is a scarce variant, produced in small numbers during brass supply shortages.',

      ctaTitle: 'Need a correct strap?',
      ctaText: 'We produce reproduction chinstraps matching the original weave, shades and hardware, period by period.',
      ctaButton: 'View chinstraps'
    },

    de: {
      progress: 'Frage {n} von {total}',
      selectAnswer: 'Bitte wählen Sie eine Antwort aus, bevor Sie fortfahren.',
      verdictLabel: 'Ergebnis',
      confidenceLabel: 'Konfidenz: ',
      yourAnswers: 'Ihre Angaben',

      confVeryHigh: 'sehr hoch',
      confHigh: 'hoch',
      confMedium: 'mittel',
      confLow: 'gering',

      lColor: 'Farbe des Gurtbands',
      lMount: 'Befestigung an der Helmglocke',
      lBuckle: 'Schnallentyp',
      lHook: 'J-Haken',
      lWeave: 'Gewebe des Gurtbands',

      vOd3: 'Olive Drab #3 (bräunliches Khaki)',
      vOd7: 'Olive Drab #7 (dunkelgrün)',
      vSewn: 'Direkt an die Bügel genäht',
      vClips: 'Abnehmbare Metallclips',
      vMountUnknown: 'Nicht feststellbar',
      vCast: 'Gussmessing (Typ M1917A1)',
      vStamped: 'Gestanztes Messing, erhabener Steg',
      vFlat: 'Flaches Messing',
      vIron: 'Eisenlegierung',
      vBuckleUnknown: 'Nicht feststellbar',
      vSharpBrass: 'Messing, spitz zulaufend',
      vSharpSteel: 'Geschwärzter Stahl, spitz zulaufend',
      vSquare: 'Quadratische Spitze',
      vHookUnknown: 'Nicht feststellbar',
      vWeave11: '11 Reihen zu je 3 Fäden',
      vWeaveOther: 'Andere Konfiguration',
      vWeaveUnknown: 'Nicht feststellbar',

      pPostwar: 'Nachkriegsproduktion',
      dPostwar: 'Abnehmbare Metallclips gibt es bei Kinnriemen des Zweiten Weltkriegs nicht: während des Krieges waren die Gurtbänder direkt an die Bügel der Helmglocke genäht. Dieses Stück ist daher nach 1945 entstanden, unabhängig von seinen übrigen Merkmalen. Kinnriemen mit Clips gehören zu den M1-Helmen des Koreakriegs und der folgenden Jahrzehnte.',

      pRepro: 'Wahrscheinlich Reproduktion',
      dRepro: 'Zwei übereinstimmende Hinweise deuten auf moderne Fertigung: Das Gewebe des Gurtbands entspricht nicht dem Original, und die erfassten Komponenten bilden eine Kombination, die es in der Kriegsproduktion nie gab. Neuere Reproduktionen übernehmen oft die auffälligsten Merkmale, ohne die tatsächliche Chronologie der Teile zu beachten.',

      pReproPossible: 'Reproduktion oder Ersatzgurtband',
      dReproPossible: 'Das Gewebe entspricht nicht den ursprünglichen 11 Reihen zu je 3 Fäden. Zwei Erklärungen sind möglich: eine moderne Reproduktion oder ein damals oder später ersetztes Gurtband. Die übrigen Merkmale bleiben untereinander stimmig, was ein teilweise authentisches Stück nicht ausschließt.',

      pComposite: 'Zusammengesetztes Stück oder Umbau',
      dComposite: 'Die erfassten Komponenten stammen aus nicht zusammenpassenden Produktionszeiträumen. Das bedeutet nicht zwangsläufig eine Reproduktion: Kinnriemen wechselten häufig den Besitzer, und Umbauten aus Teilen verschiedener Epochen sind verbreitet, sowohl während des Krieges als auch unter Sammlern. Eine Datierung einzelner Komponenten bleibt möglich, das Stück ist jedoch nicht einheitlich.',

      pVeryEarly: 'Sehr früh — 1941 oder davor',
      dVeryEarly: 'Die Schnalle aus Gussmessing stammt aus Beständen des Helms M1917A1, die bei den allerersten M1-Kinnriemen weiterverwendet wurden. Zusammen mit einem Gurtband in Olive Drab #3 verweist sie auf den Beginn der M1-Fertigung oder davor. Solche Stücke sind selten.',

      pEarly: 'Früh — 1941-1942',
      dEarly: 'Gurtband in Olive Drab #3, gestanzte Messingschnalle mit erhabenem Steg und spitz zulaufender Haken: die typische Kombination der ersten Serienfertigung, vor der Vereinfachung von 1942.',

      pMid: 'Mittel — Ende 1942 bis Mitte 1943',
      dMid: 'Die einfacher zu fertigende flache Messingschnalle löste Ende 1942 die gestanzte Schnalle ab. Zusammen mit einem Gurtband in Olive Drab #3 datiert sie diesen Kinnriemen vor den Farbwechsel zu Olive Drab #7 Ende 1943.',

      pMidLate: 'Mittel bis spät — Mitte 1943 bis Mitte 1944',
      dMidLate: 'Die Schnalle aus Eisenlegierung wurde nur zwischen Juli 1943 und Mai 1944 gefertigt, um Messing einzusparen. Sie ist das engste Datierungskriterium des gesamten Fragebogens und damit das zuverlässigste.',

      pLate: 'Spät — Mitte 1944 bis 1945',
      dLate: 'Gurtband in Olive Drab #7 und Haken mit quadratischer Spitze: die Merkmale der letzten Kriegsproduktion. Die Schnalle ist aus Messing, da die Fertigung im Juni 1944 nach dem Verzicht auf die Eisenlegierung wieder aufgenommen wurde.',

      pOd7Generic: 'Spät — Ende 1943 bis 1945',
      dOd7Generic: 'Das Gurtband in Olive Drab #7 datiert diesen Kinnriemen nach dem Farbwechsel Ende 1943. Die übrigen erfassten Merkmale erlauben keine engere Eingrenzung.',

      pOd3Generic: 'Früh bis mittel — 1941 bis 1943',
      dOd3Generic: 'Das Gurtband in Olive Drab #3 datiert diesen Kinnriemen vor den Farbwechsel Ende 1943. Die übrigen erfassten Merkmale erlauben keine engere Eingrenzung.',

      pUndetermined: 'Nicht bestimmbar',
      dUndetermined: 'Die angegebenen Merkmale reichen für eine Datierung nicht aus. Gehen Sie den Fragebogen erneut durch und betrachten Sie das Stück bei Tageslicht, oder lassen Sie es physisch begutachten.',

      nWeaveOk: 'Die 11 Reihen zu je 3 Fäden entsprechen dem ursprünglichen Gewebe.',
      nCastOd7: 'Widerspruch: Eine Gussschnalle M1917A1 auf einem Gurtband in Olive Drab #7 verbindet ein Bauteil von 1941 oder früher mit einem Gewebe von frühestens Ende 1943.',
      nSharpOd7: 'Widerspruch: Ein spitz zulaufender Haken, der 1942 aufgegeben wurde, an einem Gurtband in Olive Drab #7 von nach Ende 1943.',
      nIronOd7: 'Geringfügiger Widerspruch: Schnalle aus Eisenlegierung und Gurtband in Olive Drab #7 überschneiden sich nur in einem sehr kurzen Zeitfenster, Ende 1943 bis Mai 1944.',
      nCastSquare: 'Widerspruch: Eine Gussschnalle M1917A1 zusammen mit einem Haken mit quadratischer Spitze, der 1942 eingeführt wurde.',
      nSteelHookRare: 'Der geschwärzte Stahlhaken mit spitzem Ende ist eine seltene Variante, die in geringer Stückzahl während der Engpässe bei der Messingversorgung gefertigt wurde.',

      ctaTitle: 'Sie benötigen ein passendes Gurtband?',
      ctaText: 'Wir fertigen Reproduktions-Kinnriemen, die Gewebe, Farbtöne und Beschläge des Originals epochengetreu wiedergeben.',
      ctaButton: 'Kinnriemen ansehen'
    }
  };

  /* ---------------------------------------------------------------------
     2. LANGUE
     --------------------------------------------------------------------- */

  function detectLocale() {
    var raw = '';
    if (window.Shopify && window.Shopify.locale) raw = String(window.Shopify.locale);
    if (!raw) raw = document.documentElement.getAttribute('lang') || '';
    if (!raw) {
      var m = window.location.pathname.match(/^\/([a-zA-Z]{2})(?:-[a-zA-Z]{2})?(?:\/|$)/);
      raw = m ? m[1] : '';
    }
    raw = raw.toLowerCase();
    if (raw.indexOf('de') === 0) return 'de';
    if (raw.indexOf('en') === 0) return 'en';
    return 'fr';
  }

  var LOCALE = 'fr';

  function t(key) {
    var pack = I18N[LOCALE] || I18N.fr;
    return (key in pack) ? pack[key] : (I18N.fr[key] || '');
  }

  /* ---------------------------------------------------------------------
     3. ÉTAT ET NAVIGATION
     --------------------------------------------------------------------- */

  var STEPS = [
    'strap-quiz-intro',
    'strap-quiz-q1',
    'strap-quiz-q2',
    'strap-quiz-q3',
    'strap-quiz-q4',
    'strap-quiz-q5',
    'strap-quiz-results'
  ];

  var RESULTS_STEP = STEPS.length - 1;
  var TOTAL_QUESTIONS = 5;

  var RADIO_BY_STEP = {
    'strap-quiz-q1': 'strap-quiz-color',
    'strap-quiz-q2': 'strap-quiz-mount',
    'strap-quiz-q3': 'strap-quiz-buckle',
    'strap-quiz-q4': 'strap-quiz-hook',
    'strap-quiz-q5': 'strap-quiz-weave'
  };

  // Libellés du récapitulatif : question -> [clé d'intitulé, préfixe des valeurs]
  var SUMMARY = [
    ['strap-quiz-color',  'lColor',  { od3: 'vOd3', od7: 'vOd7' }],
    ['strap-quiz-mount',  'lMount',  { sewn: 'vSewn', clips: 'vClips', unknownMount: 'vMountUnknown' }],
    ['strap-quiz-buckle', 'lBuckle', { cast: 'vCast', stamped: 'vStamped', flat: 'vFlat', iron: 'vIron', unknownBuckle: 'vBuckleUnknown' }],
    ['strap-quiz-hook',   'lHook',   { sharpBrass: 'vSharpBrass', sharpSteel: 'vSharpSteel', square: 'vSquare', unknownHook: 'vHookUnknown' }],
    ['strap-quiz-weave',  'lWeave',  { weave11: 'vWeave11', weaveOther: 'vWeaveOther', unknownWeave: 'vWeaveUnknown' }]
  ];

  var root, current = 0, answers = {};

  function $(id) { return document.getElementById(id); }

  function goTo(index) {
    if (index < 0 || index >= STEPS.length) return;
    var prev = $(STEPS[current]);
    if (prev) prev.classList.remove('active');
    current = index;
    var next = $(STEPS[current]);
    if (next) {
      next.classList.add('active');
      next.setAttribute('tabindex', '-1');
      next.focus({ preventScroll: true });
    }
    updateProgress();
  }

  function updateProgress() {
    var el = $('strap-quiz-progress');
    if (!el) return;
    if (current === 0 || current === RESULTS_STEP) { el.textContent = ''; return; }
    el.textContent = t('progress')
      .replace('{n}', String(current))
      .replace('{total}', String(TOTAL_QUESTIONS));
  }

  function showError(stepId, message) {
    var el = $('strap-quiz-error-' + stepId.replace('strap-quiz-', ''));
    if (el) el.textContent = message || '';
  }

  function captureStep(stepId) {
    var name = RADIO_BY_STEP[stepId];
    if (!name) return true;
    var checked = root.querySelector('input[name="' + name + '"]:checked');
    if (!checked) {
      showError(stepId, t('selectAnswer'));
      return false;
    }
    showError(stepId, '');
    answers[name] = checked.value;
    return true;
  }

  /* ---------------------------------------------------------------------
     4. LOGIQUE DE DATATION
     --------------------------------------------------------------------- */

  function computeResult() {
    var color  = answers['strap-quiz-color'];
    var mount  = answers['strap-quiz-mount'];
    var buckle = answers['strap-quiz-buckle'];
    var hook   = answers['strap-quiz-hook'];
    var weave  = answers['strap-quiz-weave'];

    var notes = [];

    /* --- 1. Critère éliminatoire : clips métalliques ------------------- */
    if (mount === 'clips') {
      return {
        period: 'pPostwar',
        description: 'dPostwar',
        confidence: 'confVeryHigh',
        notes: []
      };
    }

    /* --- 2. Incohérences chronologiques ------------------------------- */
    var major = 0;

    if (buckle === 'cast' && color === 'od7') { notes.push('nCastOd7'); major++; }
    if (buckle === 'cast' && hook === 'square') { notes.push('nCastSquare'); major++; }
    if (color === 'od7' && (hook === 'sharpBrass' || hook === 'sharpSteel')) { notes.push('nSharpOd7'); major++; }
    if (buckle === 'iron' && color === 'od7') { notes.push('nIronOd7'); }

    if (hook === 'sharpSteel') notes.push('nSteelHookRare');
    if (weave === 'weave11') notes.push('nWeaveOk');

    var badWeave = (weave === 'weaveOther');

    /* --- 3. Reproduction ---------------------------------------------- */
    if (badWeave && major > 0) {
      return { period: 'pRepro', description: 'dRepro', confidence: 'confHigh', notes: notes };
    }
    if (badWeave) {
      return { period: 'pReproPossible', description: 'dReproPossible', confidence: 'confMedium', notes: notes };
    }
    if (major > 0) {
      return { period: 'pComposite', description: 'dComposite', confidence: 'confHigh', notes: notes };
    }

    /* --- 4. Datation -------------------------------------------------- */

    // Boucle en alliage de fer : fenêtre la plus étroite, elle prime.
    if (buckle === 'iron') {
      return { period: 'pMidLate', description: 'dMidLate', confidence: 'confVeryHigh', notes: notes };
    }

    if (buckle === 'cast' && color === 'od3') {
      return { period: 'pVeryEarly', description: 'dVeryEarly', confidence: 'confHigh', notes: notes };
    }

    if (color === 'od3' && buckle === 'stamped') {
      return {
        period: 'pEarly',
        description: 'dEarly',
        confidence: (hook === 'sharpBrass' || hook === 'sharpSteel') ? 'confHigh' : 'confMedium',
        notes: notes
      };
    }

    if (color === 'od3' && buckle === 'flat') {
      return { period: 'pMid', description: 'dMid', confidence: 'confHigh', notes: notes };
    }

    if (color === 'od7' && hook === 'square') {
      return {
        period: 'pLate',
        description: 'dLate',
        confidence: (buckle === 'flat' || buckle === 'stamped') ? 'confHigh' : 'confMedium',
        notes: notes
      };
    }

    // Repli sur la seule couleur, qui reste le critère le plus lisible.
    if (color === 'od7') {
      return { period: 'pOd7Generic', description: 'dOd7Generic', confidence: 'confMedium', notes: notes };
    }
    if (color === 'od3') {
      return { period: 'pOd3Generic', description: 'dOd3Generic', confidence: 'confMedium', notes: notes };
    }

    return { period: 'pUndetermined', description: 'dUndetermined', confidence: 'confLow', notes: notes };
  }

  /* ---------------------------------------------------------------------
     5. AFFICHAGE
     --------------------------------------------------------------------- */

  function renderResult(result) {
    var box   = $('strap-quiz-result-content');
    var notes = $('strap-quiz-notes');
    var list  = $('strap-quiz-answers-list');
    if (!box) return;

    box.textContent = '';
    if (notes) notes.textContent = '';
    if (list) list.textContent = '';

    var wrap = document.createElement('div');
    wrap.style.textAlign = 'center';

    var h3 = document.createElement('h3');
    h3.className = 'result-title';
    h3.textContent = t('verdictLabel');

    var p = document.createElement('p');
    p.className = 'result-period';
    p.textContent = t(result.period);

    var conf = document.createElement('p');
    conf.className = 'result-confidence';
    conf.textContent = t('confidenceLabel');
    var confVal = document.createElement('span');
    confVal.style.fontWeight = '600';
    confVal.textContent = t(result.confidence);
    conf.appendChild(confVal);

    wrap.appendChild(h3);
    wrap.appendChild(p);
    wrap.appendChild(conf);
    box.appendChild(wrap);

    var desc = document.createElement('p');
    desc.style.marginTop = '16px';
    desc.textContent = t(result.description);
    box.appendChild(desc);

    if (notes && result.notes.length) {
      result.notes.forEach(function (key) {
        var n = document.createElement('p');
        n.className = 'note-line';
        n.textContent = t(key);
        notes.appendChild(n);
      });
    }

    if (list) {
      SUMMARY.forEach(function (entry) {
        var value = answers[entry[0]];
        if (!value) return;
        var valueKey = entry[2][value];
        if (!valueKey) return;
        var li = document.createElement('li');
        var strong = document.createElement('strong');
        strong.textContent = t(entry[1]) + ' : ';
        li.appendChild(strong);
        li.appendChild(document.createTextNode(t(valueKey)));
        list.appendChild(li);
      });
    }

    renderCta();
  }

  // Renvoi produit : le texte vient du dictionnaire, l'URL du HTML.
  function renderCta() {
    var cta = $('strap-quiz-cta');
    if (!cta) return;
    var title = cta.querySelector('.cta-title');
    var text  = cta.querySelector('.cta-text');
    var link  = cta.querySelector('a');
    if (title) title.textContent = t('ctaTitle');
    if (text) text.textContent = t('ctaText');
    if (link) link.textContent = t('ctaButton');
  }

  /* ---------------------------------------------------------------------
     6. FORMULAIRE DE CAPTATION — affichage selon la langue
     --------------------------------------------------------------------- */

  function showCaptureForLocale() {
    ['fr', 'en', 'de'].forEach(function (lang) {
      var el = $('strap-capture-' + lang);
      if (el) el.style.display = (lang === LOCALE) ? '' : 'none';
    });
  }

  /* ---------------------------------------------------------------------
     7. INITIALISATION
     --------------------------------------------------------------------- */

  function reset() {
    root.querySelectorAll('input[type="radio"]').forEach(function (r) { r.checked = false; });
    Object.keys(answers).forEach(function (k) { delete answers[k]; });
    Object.keys(RADIO_BY_STEP).forEach(function (stepId) { showError(stepId, ''); });
    goTo(0);
  }

  function init() {
    root = $('strap-id-quiz');
    if (!root) return;

    LOCALE = detectLocale();
    root.setAttribute('lang', LOCALE);

    var startBtn = $('strap-quiz-start-btn');
    if (startBtn) startBtn.addEventListener('click', function () { goTo(1); });

    root.querySelectorAll('.strap-quiz-next-btn').forEach(function (btn) {
      btn.addEventListener('click', function () {
        if (!captureStep(STEPS[current])) return;
        goTo(current + 1);
      });
    });

    root.querySelectorAll('.strap-quiz-prev-btn').forEach(function (btn) {
      btn.addEventListener('click', function () {
        showError(STEPS[current], '');
        goTo(current - 1);
      });
    });

    var submitBtn = $('strap-quiz-submit-btn');
    if (submitBtn) {
      submitBtn.addEventListener('click', function () {
        if (!captureStep(STEPS[current])) return;
        renderResult(computeResult());
        goTo(RESULTS_STEP);
      });
    }

    var restartBtn = $('strap-quiz-restart-btn');
    if (restartBtn) restartBtn.addEventListener('click', reset);

    root.addEventListener('click', function (e) {
      var opt = e.target.closest ? e.target.closest('.option') : null;
      if (!opt || !root.contains(opt)) return;
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'LABEL') return;
      var input = opt.querySelector('input[type="radio"]');
      if (input) input.checked = true;
    });

    showCaptureForLocale();
    updateProgress();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

})();
